import React from "react";
import { FormattedMessage } from "react-intl";

import { Label } from "~/components/ui/label";
import type { ViewClass } from "~/features/photo-classifier/types";
import { VIEW_LABEL_ID } from "~/features/photo-classifier/views";

/**
 * The «Загрузите фотографии до лечения» protocol layout — the 8 clinical views in the shape the
 * doctors know: three across, with the SECOND row's middle cell deliberately empty so the two
 * occlusal views sit at the outer edges and the block reads as a square with a hole in it
 * (frame 5236-95279).
 *
 * `LegacyPhotoProtocol` fills each cell with `FileUpload`; the classifier uses its own card. This module
 * owns the shared row shape, labels and DOM ids while callers own cell behavior.
 */

/** Rows exactly as the frame draws them; `null` is the empty middle cell of row 2. */
export const PROTOCOL_ROWS: readonly (readonly (ViewClass | null)[])[] = [
  ["profile", "full_face_with_smile", "full_face_without_smile"],
  ["occlusal_view_upper", null, "occlusal_view_lower"],
  ["lateral_view_right", "front_view", "lateral_view_left"],
];

/**
 * The label element's DOM id per view — referenced by each slot's `aria-labelledby`, so these are a
 * stable contract, not decoration. Kept EXACTLY as the legacy markup spelled them, including
 * `lateral_view_left`'s missing `-label` suffix, because renaming one would silently break the
 * accessible name of that slot.
 */
export const SLOT_LABEL_DOM_ID: Record<ViewClass, string> = {
  profile: "photo-profile-label",
  full_face_with_smile: "photo-front-smile-label",
  full_face_without_smile: "photo-front-no-smile-label",
  occlusal_view_upper: "photo-upper-occlusal-label",
  occlusal_view_lower: "photo-lower-occlusal-label",
  lateral_view_right: "photo-lateral-right-label",
  front_view: "photo-intraoral-front-label",
  lateral_view_left: "photo-lateral-left",
};

/**
 * The legacy row spacing, preserved literally — inline, as the Bootstrap markup shipped it, which is
 * why it survives the redesign's grid rules (an inline declaration outranks a stylesheet). The
 * classifier's taller cards need an even gutter instead, so they take theirs from CSS and this map
 * applies to the legacy filling only.
 */
const LEGACY_ROW_STYLE: readonly (React.CSSProperties | undefined)[] = [undefined, { marginBottom: 10 }, { marginBottom: 10 }];

interface PhotoProtocolGridProps {
  /** Renders the cell's content under its label. */
  renderSlot: (view: ViewClass) => React.ReactNode;
  /** Marks which filling this is — the redesign CSS sizes the two cells differently. */
  variant: "legacy" | "classifier";
  testId?: string;
  required?: boolean;
}

export function PhotoProtocolGrid({ renderSlot, variant, testId, required = true }: PhotoProtocolGridProps) {
  const legacy = variant === "legacy";

  return (
    <div data-photo-protocol={variant} data-testid={testId}>
      {PROTOCOL_ROWS.map((row, rowIndex) => (
        // Rows are a fixed, static list — the index IS the identity here.
        // eslint-disable-next-line react/no-array-index-key
        <div key={rowIndex} className="row" style={legacy ? LEGACY_ROW_STYLE[rowIndex] : undefined}>
          {row.map((view, cellIndex) =>
            view ? (
              <div key={view} className="form-group col-md-4" style={legacy && rowIndex === 1 && cellIndex === 0 ? { marginBottom: 10 } : undefined}>
                <Label id={SLOT_LABEL_DOM_ID[view]} required={required} readOnly>
                  <FormattedMessage id={VIEW_LABEL_ID[view]} />
                </Label>

                {renderSlot(view)}
              </div>
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`gap-${rowIndex}-${cellIndex}`} className="form-group col-md-4" />
            ),
          )}
        </div>
      ))}
    </div>
  );
}
